import React, { useEffect, useRef, useState } from "react";
import AceEditor, { IMarker } from "react-ace";
import { format } from "sql-formatter-plus";

import { delay } from "@formant/core/common/delay";
import { fork } from "@formant/core/common/fork";
import { ISqlResult } from "@formant/core/model/ISqlResult";
import { ISqlRow } from "@formant/core/model/ISqlRow";
import { Table } from "@formant/rectangle-sdk/src/widgets/Table";
import { Typography } from "@formant/ui/component/common/Typography";

import "ace-builds/src-noconflict/ace";

import { setCompleters } from "ace-builds/src-noconflict/ext-language_tools";

import "ace-builds/src-noconflict/mode-sql";
import "ace-builds/src-noconflict/snippets/sql";
import "ace-builds/src-noconflict/theme-one_dark";

import { Button } from "@formant/ui-kit";

import { ErrorDisplay } from "../../AnalyticsChart/ErrorDisplay";
import { SqlTableViewer } from "../../AnalyticsChart/SqlTableHelper";
import { ISidebarTab, SidebarTabs } from "../../Sidebar/SidebarTabs";
import { AIInput } from "./AIInput";
import {
    ButtonBar,
    ErrorContainer,
    ExpandedContainer,
    FormContainer,
    HalfExpandedLeftContainer,
    HalfExpandedRightContainer,
    PopupWrapper,
    SubContainer,
    TableContainer,
    WideButton,
} from "./EditAnalytisModule.styles";
import { clickhouseSyntaxCompletions } from "./SqlSyntaxCompletions";

interface ISqlEditorProps {
    initialCode: string;
    className?: string;
    deviceIds: string[];
    uniqueId: string;
    onSubmit: (_: string) => void;
    executeSqlString: (_: string) => Promise<ISqlResult | undefined>;
}

export function SqlEditor(props: ISqlEditorProps) {
    const {
        initialCode,
        onSubmit,
        className,
        uniqueId,
        executeSqlString,
        deviceIds,
    } = props;
    const toFormat = (input: string) => format(input, { indent: "   " });
    const initialRowData: ISqlRow[] = [{ "Run to see results here": "" }];
    const [code, setCode] = useState<string>(toFormat(initialCode));
    const [expand, setExpand] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [rows, setRows] = useState<ISqlRow[]>(initialRowData);
    const [error, setError] = useState<string | undefined>(undefined);
    const [aiInput, setAIInput] = useState<string>("");
    const [initialUniqueId, setInitialUnitqueId] = useState<string>(uniqueId);
    const editorRef = useRef<AceEditor | null>(null);

    useEffect(() => {
        const completer = {
            getCompletions: (
                _editor: string,
                _session: string,
                _pos: string,
                _prefix: string,
                callback: (
                    source: null,
                    completions: {
                        caption: string;
                        meta: string;
                        value?: string;
                        snippet?: string;
                        docHTML?: string;
                    }[],
                ) => void,
            ) => {
                const completions = [
                    ...clickhouseSyntaxCompletions.functions.flatMap(item => ({
                        caption: item.name,
                        snippet: item.name,
                        meta: "function",
                        docHTML: `<b>${item.def}</b><hr>${item.docText}<br>&nbsp;`,
                    })),
                    ...clickhouseSyntaxCompletions.keywords.map(item => ({
                        caption: item,
                        value: item,
                        meta: "keyword",
                    })),
                    ...clickhouseSyntaxCompletions.dataTypes.map(item => ({
                        caption: item,
                        value: item,
                        meta: "keyword",
                    })),
                    ...clickhouseSyntaxCompletions.tables.map(item => ({
                        caption: item.tableName,
                        value: item.tableName,
                        meta: "table",
                    })),
                    ...clickhouseSyntaxCompletions.tables.flatMap(item =>
                        item.columns.map(column => ({
                            caption: `${item.tableName}.${column}`,
                            value: `${item.tableName}.${column}`,
                            meta: "column",
                        })),
                    ),
                    {
                        caption: "WHERE device_id IN (:DeviceIds)",
                        snippet: "WHERE device_id IN (:DeviceIds)",
                        meta: "snippet",
                        docHTML:
                            "<b>device filter context</b><hr>filters the chart by the selected devices<br>&nbsp;",
                    },
                    {
                        caption: `WHERE time >= parseDateTimeBestEffort(':StartDate') AND time <= parseDateTimeBestEffort(':EndDate')`,
                        snippet: `WHERE time >= parseDateTimeBestEffort(':StartDate') AND time <= parseDateTimeBestEffort(':EndDate')`,
                        meta: "snippet",
                        docHTML:
                            "<b>time filter </b><hr>filters the data by current time range<br>&nbsp;",
                    },
                ];
                callback(null, completions);
            },
        };

        setCompleters([completer]);
    }, []);

    function findColumnPosition(columnName: string): IMarker | undefined {
        const marker: IMarker = {
            startRow: 0,
            endRow: 0,
            startCol: 0,
            endCol: 0,
            className: "error-marker",
            type: "text",
        };
        const lines = code.split("\n");
        let found = false;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const index = line.indexOf(columnName);

            if (index !== -1) {
                marker.startRow = i;
                marker.endRow = i;
                marker.startCol = index;
                marker.endCol = index + columnName.length;
                found = true;
                break;
            }
        }

        if (!found) {
            return undefined;
        }

        return marker;
    }

    function findColumnPositionByErrorMessage(
        errorMessage?: string,
    ): IMarker | undefined {
        if (!errorMessage) {
            return undefined;
        }
        const quoteRegex = /[`']([^`']+)[`']/g;
        const match = quoteRegex.exec(errorMessage);
        if (match !== null) {
            const quotedText = match[1];
            const columnPosition = findColumnPosition(quotedText);
            if (columnPosition) {
                return columnPosition;
            }
            return undefined;
        }
    }

    useEffect(() => {
        const errorMargins = findColumnPositionByErrorMessage(error);
        editorRef.current?.handleMarkers(errorMargins ? [errorMargins] : []);
    }, [error]);

    const handleSuggestAiCode = (input: string) => {
        const formattedCode = toFormat(input);
        setCode(formattedCode);
    };

    const fetchRows = async (inputCode: string) => {
        const result = await executeSqlString(inputCode);
        if (result) {
            const { rows: newRows, error } = result;
            if (error) {
                setError(error);
                return;
            }

            if (newRows.length > 0) {
                setError(undefined);
                setRows([]);
                await delay(1);
                setRows(newRows);
            }
        }
    };

    const handleClick = async () => {
        const formattedCode = toFormat(code);
        setCode(formattedCode);
        setError(undefined);
        await fetchRows(formattedCode);
        onSubmit(formattedCode);
    };

    const handleClickAndClose = async () => {
        setExpand(false);
        await handleClick();
    };

    useEffect(() => {
        const isNewId = initialUniqueId !== uniqueId;
        // so the logic for this is
        // if the text input is dirty (text changed)
        // do not let the parent component change the
        // text. However the parent component can pass in a
        // different unique identifier to change it.
        // This to make sure the inspector can switch between
        // modules and on every module change the text is forced
        // to change.
        if (
            (!isDirty && initialCode !== "") ||
            code !== initialCode ||
            isNewId
        ) {
            setCode(toFormat(initialCode));
        }

        if (isNewId) {
            setInitialUnitqueId(uniqueId);
            setCode(toFormat(initialCode));
            fork(fetchRows(initialCode));
        }
    }, [initialCode, uniqueId]);

    const handleChange = (value: string): void => {
        setCode(value);
        setIsDirty(true);
    };

    const expandedTabs: ISidebarTab[] = [
        {
            name: "REFERENCE",
            content: <SqlTableViewer />,
            enabled: true,
        },
        {
            name: "AI ASSIST",
            icon: "ai",
            content: (
                <SubContainer>
                    <AIInput
                        value={aiInput}
                        handleChangeValue={(input: string) => setAIInput(input)}
                        deviceIds={deviceIds}
                        onSuggestCode={handleSuggestAiCode}
                    />
                </SubContainer>
            ),
            enabled: true,
        },
    ];

    const rowData: {
        [key: string]: string | number | boolean;
    }[] = rows
        .slice(0, 5)
        .map(row =>
            Object.fromEntries(
                Object.entries(row).map(([key, value]) => [
                    key,
                    value === null ? "null" : value,
                ]),
            ),
        );

    return (
        <>
            <PopupWrapper visible={expand} windowClassName="invalidate">
                <ButtonBar>
                    <Typography type="h4">SQL</Typography>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={handleClick}
                            size="sm"
                            variant="secondary"
                        >
                            Run
                        </Button>
                        <Button
                            size="sm"
                            variant="default"
                            onClick={handleClickAndClose}
                        >
                            Run & Close
                        </Button>
                    </div>
                </ButtonBar>
                <ExpandedContainer>
                    <HalfExpandedLeftContainer dark={true}>
                        <FormContainer className={className}>
                            <AceEditor
                                mode="sql"
                                theme="one_dark"
                                enableSnippets={true}
                                enableBasicAutocompletion={true}
                                enableLiveAutocompletion={true}
                                lineHeight={16.5}
                                ref={editorRef}
                                fontSize={11}
                                showPrintMargin={false}
                                value={code}
                                onChange={handleChange}
                                showGutter={false}
                                wrapEnabled={true}
                                style={{
                                    height: "25rem",
                                    fontFamily: "Fira Code",
                                    width: "24rem",
                                    maxHeight: "25rem",
                                    maxWidth: "24rem",
                                    lineHeight: "1rem",
                                }}
                            />
                        </FormContainer>
                    </HalfExpandedLeftContainer>

                    <HalfExpandedRightContainer>
                        <SidebarTabs tabs={expandedTabs} onClose={() => {}} />
                    </HalfExpandedRightContainer>
                </ExpandedContainer>
                {!error && rows && rows.length > 0 && (
                    <TableContainer>
                        <Table value={rowData} />
                    </TableContainer>
                )}

                {error && (
                    <ErrorContainer>
                        <ErrorDisplay error={error} />
                    </ErrorContainer>
                )}
            </PopupWrapper>
            <WideButton
                dark={true}
                large={false}
                onClick={() => setExpand(true)}
            >
                <Typography type="label">Edit Sql...</Typography>
            </WideButton>
        </>
    );
}
